import LocationHash from './LocationHash.js';

export default class CaseStudySearch {
  constructor(container) {
    // Container
    this.container = container;
    if (!this.container) {
      this.error(`Failed to find element #kitmap-case-study-list`);
    }

    // Form, keywords and results
    this.form = this.getElement('form');
    this.form.addEventListener('submit', this.searchRequestEventPreventDefault.bind(this));
    this.resultList = this.getElement('.result-list');
    this.keywordField = this.getElement('input[name=keywords]');
    this.spinner = this.getElement('.spinner');

    // Category
    this.category = '';
    this.categories = [];
    this.categoryButtons = this.container.querySelectorAll('.category-button');
    for (const button of this.categoryButtons) {
      let c = button.getAttribute('data-category-slug');
      if (!this.categories.includes(c)) {
        this.categories.push(c);
      }
      button.addEventListener('click', this.selectCategory.bind(this));
    }
    this.updateCategory();

    // Tag
    this.tag = null;
    this.setupTags();

    // Sort By
    this.sortBys = [
      {
        'name': 'Relevance',
        'slug': 'relevance',
        'orderBy': 'RelevanceScore',
        'orderByDirection': 'DESC',
      },
      {
        'name': 'Latest',
        'slug': 'latest',
        'orderBy': 'PublishedDate',
        'orderByDirection': 'DESC',
      },
    ]
    this.sortByDefault = this.sortBys[0];
    this.sortBy = this.sortByDefault;
    this.buttonSortByRelevant = this.getElement('.sort-by-relevant');
    this.buttonSortByRelevant.addEventListener('click', this.sortByChange.bind(this, 0));
    this.buttonSortByLatest = this.getElement('.sort-by-latest');
    this.buttonSortByLatest.addEventListener('click', this.sortByChange.bind(this, 1));

    // Page and per page
    this.page = 0;
    this.perPageDefault = 10;
    this.perPage = this.perPageDefault;
    this.paginationHolder = this.getElement('.pagination-holder');
    this.paginationPrevButton = this.getElement('.pagination-prev')
    this.paginationNextButton = this.getElement('.pagination-next')
    this.paginationPrevButton.addEventListener('click', this.paginationPrev.bind(this));
    this.paginationNextButton.addEventListener('click', this.paginationNext.bind(this));
    for (const option of this.container.querySelectorAll('.per-page-option')) {
      option.addEventListener('click', this.paginationPerPageChange.bind(this));
    }

    // Location # in the url bar stores the current state of the search and filters
    this.locationHash = new LocationHash();
    window.addEventListener('hashchange', this.locationHashToForm.bind(this));
    this.locationHashToForm();
    this.searchRequest();
  }

  /*
   * Category
   */
  selectCategory(event) {
    event.preventDefault();
    this.category = event.target.getAttribute('data-category-slug');
    this.updateCategory();
    this.searchRequest();
  }

  updateCategory() {
    for (const button of this.categoryButtons) {
      button.classList.toggle('active', button.getAttribute('data-category-slug') === this.category);
    }
  }

  /*
   * Tag
   */
  setupTags() {
    this.tags = [];
    this.tagButtons = this.container.querySelectorAll('.tag-button');
    for (const button of this.tagButtons) {
      let t = button.getAttribute('data-tag-slug');
      if (!this.tags.includes(t)) {
        this.tags.push(t);
      }
      button.onclick = this.selectTag.bind(this);
    }
    this.updateTags();
  }

  selectTag(event) {
    event.preventDefault();
    this.tag = event.target.getAttribute('data-tag-slug');
    this.updateTags();
    this.searchRequest();
  }

  updateTags() {
    for (const button of this.tagButtons) {
      button.classList.toggle('active', button.getAttribute('data-tag-slug') === this.tag);
    }
  }

  /*
   * Take the values from the location hash and put them into the form and filters
   */
  locationHashToForm() {
    var params = this.locationHash.parse();
    if (params['keywords']) {
      this.keywordField.value = params['keywords'];
    }
    this.pageUpdate(params['page']);
    this.paginationPerPageUpdate(params['per-page']);
    this.sortByFindBySlug(params['sort-by']);
    if (this.categories.includes(params['category'])) {
      this.category = params['category'];
    } else {
      this.category = '';
    }
    this.updateCategory();
    if (this.tags.includes(params['tag'])) {
      this.tag = params['tag'];
    } else {
      this.tag = '';
    }
    this.updateTags();
  }

  /*
   * Take the values the form and filters and put them into the location hash
   */
  formToLocationHash() {
    this.locationHash.set('keywords', this.keywordField.value);
    this.locationHash.set('category', this.category);
    this.locationHash.set('tag', this.tag);
    if (Number(this.page) === 0) {
      this.locationHash.set('page', null);
    } else {
      this.locationHash.set('page', this.page);
    }
    if (this.perPage === this.perPageDefault) {
      this.locationHash.set('per-page', null);
    } else {
      this.locationHash.set('per-page', this.perPage);
    }
    if (this.sortByDefault.slug === this.sortBy.slug) {
      this.locationHash.set('sort-by', null);
    } else {
      this.locationHash.set('sort-by', this.sortBy.slug);
    }
    this.locationHash.update();

    // Back to search buttons use the last saved state of the search
    sessionStorage.setItem('latestCaseStudyHash', window.location.hash);
  }

  searchRequestEventPreventDefault(event) {
    event.preventDefault();
    this.searchRequest();
  }

  searchRequest() {
    // Show spinner, hide summary, hide result list, hide pagination
    this.spinner.style.display = 'block';
    this.resultList.innerHTML = '';
    this.paginationHolder.style.display = 'none';

    this.formToLocationHash();

    // Data to send
    let data = {
      'page': this.page,
      'perPage': this.perPage,
      'orderBy': this.sortBy['orderBy'],
      'orderByDirection': this.sortBy['orderByDirection'],
      'keywords': this.keywordField.value,
      'filters': {
        'categories': this.categories.includes(this.category) && this.category !== '' ? [this.category] : [],
        'tags': this.tags.includes(this.tag) ? [this.tag] : [],
      }
    };

    // Abort any fetch calls that are in progress controller
    if (this.controller) {
      this.controller.abort();
    }
    this.controller = new AbortController();

    // Fetch
    const url = '/api/v1/case-study/search';
    const options = {
      signal: this.controller.signal,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    };

    fetch(url, options)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok ' + response.statusText);
        }
        return response.json();
      })
      .then(jsonData => {
        this.searchResponse(jsonData);
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          // Ignore aborts
        } else {
          console.error('Fetch error:', error);
        }
      });
  }

  searchResponse(data) {
    // Hide spinner, show the rest
    this.spinner.style.display = 'none';
    this.resultList.innerHTML = '';
    this.paginationHolder.style.display = 'none';

    // Result list
    if (data.total === 0) {
      this.resultList.innerHTML = 'No results found';
    } else {
      this.resultList.innerHTML = data.results.join('');
    }
    this.setupTags();

    // Pagination
    this.page = data.page;
    this.paginationPerPageUpdate(data.perPage);
    this.paginationHolder.style.display = data.total === 0 ? 'none' : 'flex';

    let paginationSummary = '';
    if (data.total === 1) {
      paginationSummary = 'Showing 1 result';
    } else if (data.total <= data.perPage) {
      paginationSummary = 'Showing ' + data.total + ' results';
    } else {
      let summaryFrom = Math.min(this.page * data.perPage + 1, data.total);
      let summaryTo = Math.min((this.page + 1) * data.perPage, data.total);
      paginationSummary = 'Showing <span>' + summaryFrom + '</span> to <span>' + summaryTo + '</span> of <span>' + data.total + '</span> results';
    }
    this.getElement('.pagination-summary').innerHTML = paginationSummary;

    if (data.total <= data.perPage) {
      this.paginationPrevButton.style.display = 'none';
      this.paginationNextButton.style.display = 'none';
    } else if (this.page === 0) {
      this.paginationPrevButton.style.display = 'none';
      this.paginationNextButton.style.display = 'flex';
      this.paginationNextButton.classList.remove('rounded-e');
      this.paginationNextButton.classList.add('rounded');
    } else if (this.page === data.pageMax) {
      this.paginationPrevButton.style.display = 'flex';
      this.paginationPrevButton.classList.remove('rounded-s');
      this.paginationPrevButton.classList.add('rounded');
      this.paginationNextButton.style.display = 'none';
    } else {
      this.paginationPrevButton.style.display = 'flex';
      this.paginationPrevButton.classList.remove('rounded');
      this.paginationPrevButton.classList.add('rounded-s');
      this.paginationNextButton.style.display = 'flex';
      this.paginationNextButton.classList.remove('rounded');
      this.paginationNextButton.classList.add('rounded-e');
    }
  }

  /*
   * Sort and order by
   *
   * "Order By" comes from MySql conventions, its the column and the direction.
   * "Sort By" comes from the user interface, and human friendly names.
   */
  sortByChange(index, event) {
    this.sortByUpdate(this.sortBys[index]);
    this.searchRequest();
  }

  sortByUpdate(newSortBy) {
    this.sortBy = newSortBy;
    this.buttonSortByRelevant.classList.toggle('active', this.sortBy.slug === 'relevance');
    this.buttonSortByLatest.classList.toggle('active', this.sortBy.slug === 'latest');
    //this.getElement('.sort-by-current-name').innerHTML = this.sortBy.name;
  }

  sortByFindBySlug(slug) {
    let newSortBy = this.sortByDefault;
    for (const o of this.sortBys) {
      if (o.slug === slug) {
        newSortBy = o;
      }
    }
    this.sortByUpdate(newSortBy);
  }

  /*
   * Pagination
   */
  paginationPrev() {
    this.page -= 1;
    this.searchRequest();
  }

  paginationNext() {
    this.page += 1;
    this.searchRequest();
  }

  paginationPerPageChange(event) {
    this.paginationPerPageUpdate(event.target.innerHTML);
    // Flowbite needs this to close the dropdown after clicking on an option
    this.getElement('#per-page-button').click();
  }

  paginationPerPageUpdate(n) {
    n = Number(n);
    if (Number.isNaN(n)) {
      n = 10;
    }
    //this.getElement('.per-page-current-name').innerHTML = n;
    this.perPage = n;
  }

  pageUpdate(n) {
    n = Number(n);
    if (Number.isNaN(n)) {
      n = 0;
    }
    this.page = n;
  }

  /*
   * Helpers
   */
  getElement(query) {
    const element = this.container.querySelector(query);
    if (!element) {
      this.error(`Failed to find the element with '${query}'`);
    }
    return element;
  }

  error(message) {
    console.error('Kitmap Directory', message);
  }

  log(message) {
    console.log('Kitmap Directory', message);
  }
}
